import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

const EmpowerStyles = css({
    ["@keyframes progressgrow"]: {
        ["0%"]: {
            height: "0%"
        },
        ["100%"]: {
            height: "100%"
        }
    },
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    paddingBlock: "50px",
    marginInline: "32px",
    [media.below.lg]: {
        paddingBlock: "32px",
        marginInline: "0px"
    },

    [".row-width"]: {
        maxWidth: "1187px",
        display: "flex",
        flexWrap: "wrap",
        gap: "32px",

        [media.below.lg]: {
            width: "100%",
            flexDirection: "column",
            alignItems: "center",
            justifyItems: "center",
            gap: "0px"
        },

        [".half"]: {
            flexBasis: "calc(50% - 16px)"
        }
    },
    h2: {
        fontWeight: "700",
        fontSize: "48px",
        lineHeight: "56px",
        margin: "0 0 30px",
        [media.below.lg]: {
            fontSize: "30px",
            lineHeight: "36px",
            margin: "0"
        }
    },

    [".top-section"]: {
        textAlign: "center",
        [media.below.lg]: {
            marginInline: "10%"
        }
    },
    [".mobile-images"]: {
        display: "none",
        [media.below.lg]: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
        }
    },
    [".desktop-images"]: {
        display: "block",
        [media.below.lg]: {
            display: "none"
        }
    },
    [".bottom-section"]: {
        [media.below.lg]: {
            marginInline: "10%",
            paddingBlockStart: "32px"
        },

        paddingBlockStart: "50px",
        background: "#fff",
        [".half"]: {
            position: "relative",
            ["&.images"]: {
                aspectRatio: "5/4"
            }
        },
        input: {
            fontFamily: "inherit",
            fontSize: "1rem",
            padding: "8px",
            background: "#FAFBFC",
            border: "2px solid #DFE1E6",
            margin: "0 10px 0 0"
        },
        img: {
            transition: "opacity 0.5s",
            position: "absolute",
            width: "100%",
            height: "auto"
        },
        [".option-container"]: {
            img: {
                position: "relative",
                maxWidth: "600px"
            }
        },

        [".option"]: {
            margin: "0 0 32px",
            padding: "0 0 0 20px",
            position: "relative",
            height: "100%",
            [".progress"]: {
                position: "absolute",
                insetInlineStart: 0,
                insetBlockStart: 0,
                insetBlockEnd: 0,
                width: "3px",
                height: "32px",
                background: "#C1C7D0",
                div: {
                    background: "#0065FF",
                    height: "0%"
                }
            },
            h3: {
                fontSize: "24px",
                lineHeight: "30px",
                fontWeight: "700",
                margin: 0,
                transition: "font-size 0.5s",
                [media.below.lg]: {
                    fontWeight: "600"
                }
            },
            button: {
                background: "none",
                appearance: "none",
                inset: 0,
                position: "absolute",
                cursor: "pointer",
                border: 0
            },
            ["&.active"]: {
                h3: {
                    fontSize: "32px",
                    lineHeight: "38px",
                    [media.below.lg]: {
                        fontSize: "24px",
                        lineHeight: "30px"
                    }
                },
                p: {
                    maxHeight: "150px"
                },
                [".progress"]: {
                    height: "auto",
                    div: {
                        animation: "progressgrow linear 5s 1"
                    }
                }
            },
            p: {
                transition: "max-height 0.5s",
                maxHeight: 0,
                margin: 0,
                overflow: "hidden"
            }
        },
        button: {
            background: "none",
            fontSize: "1.25rem",
            appearance: "none",
            padding: "10px 30px",
            fontFamily: "inherit",
            border: "2px solid"
        }
    }
});

type OptionsTypes = {
    title: string;
    image: string;
    description: string;
};

type JswSectionCollaborateTypes = {
    heading: string;
    options: OptionsTypes[];
};

export const JswCollaborate = ({
    heading,
    options
}: JswSectionCollaborateTypes) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    const [activeOption, setActiveOption] = useState(0);

    const changeActive = (index: number) => {
        // Cancel all animations before activeOption change
        const progressGrowAnimations = document
            .getAnimations()
            .filter((animation) => {
                return (
                    animation instanceof CSSAnimation &&
                    animation.animationName === "progressgrow"
                );
            });
        progressGrowAnimations.forEach((animation) => {
            animation.cancel();
        });

        setActiveOption(index);
    };

    useEffect(() => {
        const progressGrowAnimations = document
            .getAnimations()
            .filter((animation) => {
                return (
                    animation instanceof CSSAnimation &&
                    animation.animationName === "progressgrow"
                );
            });

        // set finish event
        progressGrowAnimations.forEach((animation) => {
            animation.onfinish = () => {
                setActiveOption(
                    activeOption == options.length - 1 ? 0 : activeOption + 1
                );
                animation.cancel();
            };
        });

        // Only cycle when visible
        if (!sectionIsVisible) {
            progressGrowAnimations.forEach((animation) => {
                animation.pause();
            });
        } else {
            progressGrowAnimations.forEach((animation) => {
                animation.play();
            });
        }
    }, [activeOption, options.length, sectionIsVisible]);

    return (
        <div
            css={EmpowerStyles}
            ref={scrollRef}
            className={sectionIsVisible ? "is-visible" : ""}
        >
            <div className="top-section">
                <h2>{heading}</h2>
            </div>
            <div className="bottom-section">
                <div className="row-width">
                    <div className="half texts">
                        {options.map((option, index) => {
                            return (
                                <div className="option-container" key={index}>
                                    <div
                                        className={`option ${
                                            activeOption == index
                                                ? "active"
                                                : "inactive"
                                        }`}
                                    >
                                        <div className="progress">
                                            <div />
                                        </div>
                                        <h3>{option.title}</h3>
                                        <p>{option.description}</p>
                                        <button
                                            aria-label="expand jira benefit"
                                            onClick={() => {
                                                changeActive(index);
                                            }}
                                        />
                                    </div>
                                    <div className="mobile-images">
                                        <img
                                            alt={option.title}
                                            style={{
                                                display:
                                                    activeOption == index
                                                        ? "block"
                                                        : "none"
                                            }}
                                            key={index}
                                            src={option.image}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="half images">
                        {options.map((option, index) => {
                            return (
                                <div key={index} className="desktop-images">
                                    <img
                                        alt={option.title}
                                        style={{
                                            opacity:
                                                activeOption == index
                                                    ? "1"
                                                    : "0"
                                        }}
                                        src={option.image}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
